import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import { API_BASE_URL } from "../api";

const letterRegex = /[A-Za-zА-Яа-я]/;
const digitRegex = /\d/;

export default function ResetPasswordPage({ navigate, user, onLogin, onAccount, showBlog = true, onShareSite }) {
  const [params, setParams] = useState({ selector: "", token: "" });
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "СоцПлан | Новый пароль";
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const selector = (query.get("selector") || "").trim();
    const token = (query.get("token") || "").trim();
    if (!selector || !token) {
      navigate("resetPasswordInvalid");
      return;
    }
    setParams({ selector, token });
    setReady(true);
  }, [navigate]);

  const rules = useMemo(() => {
    const lengthOk = password.length >= 8;
    const hasLetter = letterRegex.test(password);
    const hasDigit = digitRegex.test(password);
    const noSpaces = !/\s/.test(password);
    return [
      { label: "Минимум 8 символов", ok: lengthOk },
      { label: "Содержит буквы", ok: hasLetter },
      { label: "Содержит цифры", ok: hasDigit },
      { label: "Без пробелов", ok: noSpaces },
    ];
  }, [password]);

  const validate = () => {
    if (password.length < 8) return "Пароль должен быть не короче 8 символов";
    if (!digitRegex.test(password)) return "Пароль должен содержать цифры";
    if (!letterRegex.test(password)) return "Пароль должен содержать буквы";
    if (/\s/.test(password)) return "Пароль не должен содержать пробелы";
    if (password !== confirm) return "Пароли не совпадают";
    return null;
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(API_BASE_URL + "/auth/password/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selector: params.selector,
          token: params.token,
          newPassword: password,
        }),
      });
      if (!res.ok) {
        let data = null;
        try {
          data = await res.json();
        } catch (_) {
          data = null;
        }
        if (data?.error === "invalid_token") {
          navigate("resetPasswordInvalid");
          return;
        }
        if (data?.error === "weak_password") {
          setError(data?.message || "Пароль не соответствует требованиям");
          setSubmitting(false);
          return;
        }
        setError("Не удалось сменить пароль. Попробуйте ещё раз");
        setSubmitting(false);
        return;
      }
      navigate("resetPasswordSuccess");
    } catch (err) {
      console.error("[Reset password]", err);
      setError("Не удалось сменить пароль. Попробуйте ещё раз");
      setSubmitting(false);
    }
  };

  if (!ready) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-100 py-6 md:py-10">
      <div className="mx-auto w-full max-w-3xl rounded-2xl border bg-white shadow-md">
        <Header
          navigate={navigate}
          user={user}
          onLogin={onLogin}
          onAccount={onAccount}
          showBlog={showBlog}
          onShareSite={onShareSite}
        />
        <section className="px-5 md:px-6 py-8">
          <h1 className="text-2xl font-semibold">Установите новый пароль</h1>
          <p className="mt-3 text-slate-600">
            Придумайте надёжный пароль. После сохранения вы сможете войти в аккаунт.
          </p>
          <form onSubmit={submit} className="mt-6 max-w-xl space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700" htmlFor="new-password">
                Новый пароль
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                className="mt-1 w-full rounded-lg border px-3 py-2 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setError("");
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700" htmlFor="confirm-password">
                Повторите пароль
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                className="mt-1 w-full rounded-lg border px-3 py-2 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={confirm}
                onChange={(event) => {
                  setConfirm(event.target.value);
                  setError("");
                }}
              />
            </div>
            <ul className="mt-3 space-y-1 text-sm">
              {rules.map((rule) => (
                <li key={rule.label} className={rule.ok ? "text-green-600" : "text-slate-500"}>
                  {rule.ok ? "✓" : "•"} {rule.label}
                </li>
              ))}
            </ul>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-6 font-semibold text-white shadow hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting ? "Сохраняем..." : "Сохранить пароль"}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
